import { Link, useNavigate } from "react-router-dom"
import { Footer } from "../../../components/landing"
import { EffectProvider } from "../../../effects"
import { PRICING_FAQ, PRICING_PLANS } from "../../../data/pricing"
import { useBrand } from "../../../theme/useBrand"
import styles from "./Pricing.module.css"

const MONO = '"IBM Plex Mono","Space Mono",ui-monospace,Menlo,Consolas,monospace'

export function PricingPage() {
  const navigate = useNavigate()
  const brand = useBrand()

  return (
    <EffectProvider>
      <main
        style={{
          minHeight: "100vh",
          background: "var(--bg)",
          color: "var(--text)",
          fontFamily: MONO,
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            padding: "18px clamp(16px, 4vw, 32px)",
            borderBottom: "1px solid var(--rail)",
          }}
        >
          <Link
            to="/"
            aria-label={`${brand.name} home`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              color: "var(--text)",
              textDecoration: "none",
              letterSpacing: "0.18em",
              fontSize: "12px",
              textTransform: "uppercase",
            }}
          >
            <img src={brand.sprite} width={22} height={22} className="pixel" alt="" />
            {brand.wordmark}
          </Link>
          <nav style={{ display: "flex", gap: "18px", alignItems: "center" }}>
            <Link
              to="/how-it-works"
              style={{ color: "var(--muted)", textDecoration: "none", fontSize: "12px", letterSpacing: "0.14em", textTransform: "uppercase" }}
            >
              How it works
            </Link>
            <Link
              to="/login"
              style={{ color: "var(--muted)", textDecoration: "none", fontSize: "12px", letterSpacing: "0.14em", textTransform: "uppercase" }}
            >
              Login
            </Link>
          </nav>
        </header>

        <div style={{ width: "min(1220px, 100%)", margin: "0 auto", padding: "clamp(32px, 6vw, 64px) clamp(16px, 4vw, 32px) 64px" }}>
          <section style={{ textAlign: "center", marginBottom: "clamp(32px, 6vw, 56px)" }}>
            <p style={{ margin: 0, color: "var(--accent-text)", fontSize: "11px", letterSpacing: "0.22em", textTransform: "uppercase" }}>
              Simple pricing
            </p>
            <h1 style={{ margin: "14px 0 10px", fontSize: "clamp(28px, 5vw, 44px)", letterSpacing: "0.04em", lineHeight: 1.15 }}>
              Pick a plan. Launch in minutes.
            </h1>
            <p style={{ margin: "0 auto", maxWidth: "52ch", color: "var(--muted)", fontSize: "14px", lineHeight: 1.7 }}>
              Every plan includes the full invoicing agent — invoice by text, automatic follow-ups,
              and a dashboard that fills itself in. 14-day free trial, no credit card.
            </p>
          </section>

          <section
            aria-label="Plans"
            className={styles.planGrid}
          >
            {PRICING_PLANS.map((plan) => (
              <article
                key={plan.name}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                  padding: "24px",
                  border: plan.featured ? "1px solid var(--accent)" : "1px solid var(--rail)",
                  boxShadow: plan.featured ? "0 0 0 1px var(--accent)" : "none",
                  background: "var(--panel)",
                  position: "relative",
                }}
              >
                {plan.featured ? (
                  <span
                    style={{
                      position: "absolute",
                      top: "-11px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "var(--accent)",
                      color: "var(--text-on-accent)",
                      fontSize: "10px",
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      padding: "4px 10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Most popular
                  </span>
                ) : null}
                <h2 style={{ margin: 0, fontSize: "14px", letterSpacing: "0.2em", textTransform: "uppercase" }}>{plan.name}</h2>
                <p style={{ margin: 0, fontSize: "40px", fontWeight: 700, lineHeight: 1 }}>
                  ${plan.price}
                  <span style={{ fontSize: "13px", fontWeight: 400, color: "var(--muted)" }}>/month</span>
                </p>
                <p style={{ margin: 0, color: "var(--muted)", fontSize: "12.5px" }}>{plan.subtitle}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "8px", flex: 1, alignContent: "start" }}>
                  {plan.features.map((feature) => (
                    <li key={feature} style={{ fontSize: "13px", lineHeight: 1.5 }}>
                      <span style={{ color: "var(--accent-text)" }}>✓</span> {feature}
                    </li>
                  ))}
                </ul>
                <p style={{ margin: 0, color: "var(--muted)", fontSize: "11.5px", lineHeight: 1.5 }}>{plan.note}</p>
                <button
                  type="button"
                  onClick={() => navigate("/onboard/contact")}
                  className="mono"
                  style={{
                    border: plan.featured ? "1px solid var(--accent)" : "1px solid var(--rail)",
                    background: plan.featured ? "var(--accent)" : "transparent",
                    color: plan.featured ? "var(--text-on-accent)" : "var(--text)",
                    padding: "14px",
                    fontSize: "12px",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    minHeight: "48px",
                  }}
                >
                  {plan.cta}
                </button>
              </article>
            ))}
          </section>

          <section aria-label="Messaging costs" style={{ marginBottom: "clamp(40px, 7vw, 72px)" }}>
            <div
              style={{
                border: "1px dashed var(--accent)",
                padding: "16px 20px",
                fontSize: "12.5px",
                lineHeight: 1.7,
                color: "var(--muted)",
              }}
            >
              <span style={{ color: "var(--accent-text)", textTransform: "uppercase", letterSpacing: "0.14em" }}>Messaging costs:</span>{" "}
              plans include monthly message allowances; beyond that we meter SMS usage and pass through
              carrier fees with zero markup. No surprise line items.
            </div>
          </section>

          <section aria-label="Frequently asked questions" style={{ marginBottom: "clamp(40px, 7vw, 72px)" }}>
            <h2 style={{ margin: "0 0 18px", fontSize: "16px", letterSpacing: "0.2em", textTransform: "uppercase" }}>Questions</h2>
            <div style={{ display: "grid", gap: "10px" }}>
              {PRICING_FAQ.map((item) => (
                <details
                  key={item.question}
                  style={{ border: "1px solid var(--rail)", background: "var(--panel)", padding: "14px 18px" }}
                >
                  <summary style={{ cursor: "pointer", fontSize: "13px", letterSpacing: "0.04em" }}>{item.question}</summary>
                  <p style={{ margin: "10px 0 0", color: "var(--muted)", fontSize: "13px", lineHeight: 1.7 }}>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>

          <section aria-label="Get started" style={{ textAlign: "center" }}>
            <h2 style={{ margin: "0 0 12px", fontSize: "clamp(20px, 3.5vw, 28px)", letterSpacing: "0.06em" }}>
              Ready when you are.
            </h2>
            <p style={{ margin: "0 0 20px", color: "var(--muted)", fontSize: "13px" }}>
              Two-minute setup. Your agent texts you first.
            </p>
            <button
              type="button"
              onClick={() => navigate("/onboard/contact")}
              className="mono"
              style={{
                border: "1px solid var(--accent)",
                background: "var(--accent)",
                color: "var(--text-on-accent)",
                padding: "16px 28px",
                fontSize: "13px",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                cursor: "pointer",
                minHeight: "52px",
              }}
            >
              Get started
            </button>
          </section>
        </div>

        <Footer />
      </main>
    </EffectProvider>
  )
}

export default PricingPage
